/**
 * 옵션 타입별 핸들러의 기본 클래스
 * 각 옵션 타입별 핸들러는 이 클래스를 상속받아 구현
 */
import { deepSnakeToCamel } from '../../../utils/utils';
import { cloneDeep } from 'lodash';

export class BaseOptionHandler {
    constructor({
        itemId,
        productInfo,
        fetchOptionVariant,
        setToastMessage,
        setIsToastVisible,
        setTotalQuantity,
        setTotalPrice,
        setOptionSelectedProductList,
    }) {
        this.itemId = itemId;
        this.productInfo = productInfo;
        this.fetchOptionVariant = fetchOptionVariant;
        this.setToastMessage = setToastMessage;
        this.setIsToastVisible = setIsToastVisible;
        this.setTotalQuantity = setTotalQuantity;
        this.setTotalPrice = setTotalPrice;
        this.setOptionSelectedProductList = setOptionSelectedProductList;
    }

    MAX_QUANTITY = 10;
    MAX_QUANTITY_TOAST_MESSAGE = '한번에 최대 10개까지 담을 수 있어요';

    initialProductOptions = []; // 최초 옵션 상태 복사해서 저장해두기

    /**
     * 옵션 데이터 초기화
     * @param {Array} options - API에서 받은 옵션 데이터
     * @returns {Array} 초기화된 옵션 데이터
     */
    initializeOptions(options) {
        const selectOptions = options.filter(option => (!option.isTextOption && !option.isFileOption));
        //const textAndFileOptions = options.filter(option => (option.isTextOption || option.isFileOption));
        const textAndFileOptions = options.filter(option => option.isTextOption);
        
        return {
            initializedSelectOptions: selectOptions.map(option => ({
                ...option,
                selectedIndex: null,
                selectedValue: null,
                value: option.value.filter(value => {
                    return value.hasVariant
                }),
                isFocused: false
            })),
            initializedTextAndFileOptions: textAndFileOptions.map(option => ({
                ...option,
                selectedIndex: null,
                selectedValue: option.isTextOption ? '' : null,
                value: option.isTextOption ? '' : null,
                isFocused: false
            })),
        };
    }

    infoFetchFailedToast() {
        this.setToastMessage('상품 정보를 불러오는데 실패했어요');
        this.setIsToastVisible(true);
    }

    maxNumberOfProductsToast() {
        this.setToastMessage(this.MAX_QUANTITY_TOAST_MESSAGE);
        this.setIsToastVisible(true);
    }

    combinationAlreadyExistsToast() {
        this.setToastMessage('이미 추가된 품목이에요');
        this.setIsToastVisible(true);
    }

    /**
     * fetchOption 직후 실행할 로직
     * 각 타입별로 다르게 구현
     * @param {Array} options - 초기화된 옵션 데이터
     * @param {Function} setProductOptions - 옵션 상태 업데이트 함수
     * @param {Function} setOptionSelectedProductList - 선택된 상품 리스트 업데이트 함수
     */
    async onOptionsLoaded(props) {
        const { 
            selectOptions, 
            textAndFileOptions, 
            allVariantsResponse, 
            setProductOptions, 
            setTextAndFileOptions, 
            setAllVariants,
            setOptionSelectedProductList 
        } = props;
        // options는 이미 initializeOptions가 호출된 상태로 전달됨
        setProductOptions(selectOptions);
        setTextAndFileOptions(textAndFileOptions);
        setAllVariants(allVariantsResponse);

        this.initialProductOptions = cloneDeep(selectOptions);

        // select options가 없고 text/file options만 있는 경우, 기본 product를 만든다
        if (selectOptions.length === 0 && textAndFileOptions.length > 0) {
            const onlyVariant = allVariantsResponse[0];
            if (!onlyVariant || !onlyVariant?.variantId || onlyVariant?.variantId?.length === 0) {
                this.infoFetchFailedToast();
                return;
            }
            setOptionSelectedProductList(prev => [...prev, {
                productName: this.productInfo?.name,
                combination: 'default',
                noOptionText: true,
                optionValueList: [],
                textOptions: textAndFileOptions,
                quantity: 1,
                setQuantity: (quantity) => this.handleQuantityChange('default', quantity),
                price: this.productInfo?.price,
                additionalPrice: onlyVariant?.additionalPrice || 0,
                processProductDeleteClicked: () => this.handleProductDeleteClicked('default'),
                variantId: onlyVariant.variantId,
            }]);
        }
    }

    /**
     * 옵션 선택 시 실행할 로직
     * 각 타입별로 다르게 구현
     * @param {number} optionIndex - 선택된 옵션의 인덱스
     * @param {number} selectedIdx - 선택된 값의 인덱스
     * @param {Array} productOptions - 현재 옵션 상태
     * @param {Function} setProductOptions - 옵션 상태 업데이트 함수
     * @param {Function} setOptionSelectedProductList - 선택된 상품 리스트 업데이트 함수
     */
    async onOptionSelect(props) {
        const { 
            optionIndex, 
            selectedIdx, 
            productOptions, 
            renderableOptions, 
            setProductOptions, 
            setRenderableOptions, 
            setOptionSelectedProductList
        } = props;
        // 기본 구현
        setProductOptions(prev => 
            prev.map((option, index) => 
                index === optionIndex 
                    ? { 
                        ...option, 
                        selectedIndex: selectedIdx,
                        selectedValue: option.value[selectedIdx]?.optionText || '---'
                    }
                    : option
            )
        );
    }

    /**
     * 렌더링할 옵션 리스트 반환
     * @param {Array} productOptions - 현재 옵션 상태
     * @returns {Array} 렌더링할 옵션 리스트
     */
    getRenderableOptions(productOptions, allVariants) {
        // 기본 구현: 모든 옵션 반환
        return productOptions;
    }

    async onAddToCartClicked(props) {
        const {
            postCafe24ProductStockCheck,
            partnerId,
            optionSelectedProductList,
            productOptions,
            renderableOptions,
            sendAddProductWithOptionsToCart,
            chatbot,
            chatUserId,
            navigate
        } = props;

        const isProductSelling = await postCafe24ProductStockCheck(partnerId, this.productInfo.itemId);
        if (isProductSelling?.selling === "F") {
            this.setToastMessage('품절된 상품입니다');
            this.setIsToastVisible(true);
            sessionStorage.removeItem('product-option');
            navigate(`/chat/${chatbot.chatbotId}/${chatUserId}`)
            return;
        }

        // 필수 옵션을 선택 안 했거나, 필수인 textOption 을 입력 안 했으면 경고
        const hasRequiredOptionSelectedProduct = optionSelectedProductList.filter(product => !product.noOptionText).length > 0;
        const hasEmptyRequiredTextOptionInput = optionSelectedProductList.filter(product => {
            return product.textOptions.some(textOption => textOption.required && textOption.selectedValue === '');
        }).length > 0;
        if (productOptions.length > 0 && !hasRequiredOptionSelectedProduct) {
            this.setToastMessage('필수 옵션을 선택해주세요');
            this.setIsToastVisible(true);
            return;
        }

        if (hasEmptyRequiredTextOptionInput) {
            this.setToastMessage('필수 옵션을 입력해주세요');
            this.setIsToastVisible(true);
            return;
        }
        sendAddProductWithOptionsToCart(this.productInfo, optionSelectedProductList);
        sessionStorage.removeItem('product-option');
        navigate(`/chat/${chatbot.chatbotId}/${chatUserId}`)
    }

    // 수량 변경 핸들러
    handleQuantityChange(combination, quantity) {
        // 함수형 업데이트를 사용하여 최신 상태 참조
        this.setOptionSelectedProductList(prev => {
            const changedProduct = prev.find(product => product.combination === combination);
            if (!changedProduct) {
                console.warn(`Product with combination ${combination} not found`);
                return prev;
            }

            // 현재 총 수량 계산
            const currentTotalQuantity = prev.reduce((acc, product) => acc + product.quantity, 0);

            // 총 수량 및 가격 업데이트
            const newTotalQuantity = currentTotalQuantity - changedProduct.quantity + quantity;
            const currentTotalPrice = prev.reduce((acc, product) => acc + (product.price + (product.additionalPrice || 0)) * product.quantity, 0);
            const newTotalPrice = currentTotalPrice - (changedProduct.price + (changedProduct.additionalPrice || 0)) * changedProduct.quantity + (changedProduct.price + (changedProduct.additionalPrice || 0)) * quantity;
            
            this.setTotalQuantity(newTotalQuantity);
            this.setTotalPrice(newTotalPrice);

            // 옵션 선택 상품 리스트 업데이트
            return prev.map(product => 
                product.combination === combination 
                    ? { ...product, quantity: quantity } 
                    : product
            );
        });
    }

    // 상품 삭제 핸들러
    handleProductDeleteClicked(combination) {
        // 함수형 업데이트를 사용하여 최신 상태 참조
        this.setOptionSelectedProductList(prev => {
            const deletedProduct = prev.find(product => product.combination === combination);
            if (!deletedProduct) {
                console.warn(`Product with combination ${combination} not found`);
                return prev;
            }

            // 총 수량 및 가격 업데이트
            const newTotalQuantity = prev.reduce((acc, product) => acc + product.quantity, 0) - deletedProduct.quantity;
            const currentTotalPrice = prev.reduce((acc, product) => acc + (product.price + (product.additionalPrice || 0)) * product.quantity, 0);
            const newTotalPrice = currentTotalPrice - (deletedProduct.price + (deletedProduct.additionalPrice || 0)) * deletedProduct.quantity;
            
            this.setTotalQuantity(newTotalQuantity);
            this.setTotalPrice(newTotalPrice);

            // 옵션 선택 상품 리스트에서 제거
            return prev.filter(product => product.combination !== combination);
        });
    }
}

